import { Component, OnInit, Input, Output, EventEmitter, ViewChild, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { EFormConfiguration, enmEFormViewModes } from '../../../shared-components/e-forms/e-form-config.model';
import { CSTCustomPage } from '../models/cst-custom-page.model';
import { ErrorList } from '../../../shared-components/controls/error-prompt/error-list.model';
import { EFormViewerComponent } from '../../../shared-components/e-forms/e-form-viewer/e-form-viewer.component';
import { CustomizationFrameworkService } from '../customization-framework.service';
import { EFormService } from '../../../shared-components/e-forms/e-form.service';
import { EFormDataObject } from '../../../shared-components/e-forms/e-formDataObject.model';
import { EFormControl } from '../../../shared-components/e-forms/e-form-control.model';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { CSTField, CustomFieldDataObject } from '../models/custom-data-field.model';
import * as _ from 'lodash';
import { YearService } from '../../../shared-components/year/year.service';
import { take } from 'rxjs/operators';

@AutoUnsubscribe({ includeArrays: true })
@Component({
    selector: 'custom-page-viewer',
    templateUrl: './custom-page-viewer.component.html',
    styleUrls: ['./custom-page-viewer.component.css']
})
export class CustomPageViewerComponent implements OnInit, OnDestroy {

    subscriptions: Subscription[] = [];
    validationTriggerSubscription: Subscription;
    saveTriggerSubscription: Subscription;
    config: EFormConfiguration = new EFormConfiguration();
    customPageIndex: number;
    showViewer = false;
    initialized = false;
    showErrorsOnInitialize = false;
    dataInitialized = false;

    private _canShowForm: boolean;
    public get canShowForm(): boolean {
        return this._canShowForm;
    }
    public set canShowForm(value: boolean) {
        this._canShowForm = value;
        if (value == true && this.initialized) {
            this.loadViewer();
            this.showViewer = value;
        }
        else if (value == false) {
            this.showViewer = false;
        }
    }

    private _customPage: CSTCustomPage;
    get customPage(): CSTCustomPage {
        return this._customPage;
    }

    @Input() set customPage(value: CSTCustomPage) {
        this._customPage = value;
        if (this.initialized) {
            this.loadViewer();
        }
    }

    // this is the identifying information used to get/set the data (studentID, FamilyID, etc)
    private _masterId: number;
    get masterId(): number {
        return this._masterId;
    }
    @Input() set masterId(value: number) {
        this._masterId = value;
    }


    @Input() hideTitle = false;
    @Input() set validationTrigger(trigger: EventEmitter<any>) {
        if (this.validationTriggerSubscription) {
            const subIndex = this.subscriptions.indexOf(this.validationTriggerSubscription);
            if (subIndex > -1) {
                this.subscriptions.splice(subIndex, 1);
            }
            this.validationTriggerSubscription.unsubscribe();
        }
        this.validationTriggerSubscription = trigger.subscribe(o => {
            const errors = this.validateControls();
            this.validationErrors.emit(errors);
        });
        this.subscriptions.push(this.validationTriggerSubscription);
    }

    private _readOnly: boolean;
    public get readOnly(): boolean {
        return this._readOnly;
    }
    @Input() public set readOnly(value: boolean) {
        this._readOnly = value;
        if (this.config) {
            this.config.ReadOnly = value;
        }
        if (this.eFormViewer && !value) {
            this.eFormViewer.editable = false;
        }
    }

    @Output() validationErrors: EventEmitter<ErrorList[]> = new EventEmitter<ErrorList[]>();

    private _isDirty: boolean;
    public get isDirty(): boolean {
        return this._isDirty;
    }
    @Input() public set isDirty(value: boolean) {
        this._isDirty = value;
    }
    @Output() isDirtyChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild(EFormViewerComponent) eFormViewer: EFormViewerComponent;

    private _dataStore: CustomFieldDataObject[] = [];
    public get dataStore(): CustomFieldDataObject[] {
        return this._dataStore;
    }
    @Input() public set dataStore(value: CustomFieldDataObject[]) {
        this._dataStore = value;
        if (this.config) {
            this.config.CFDataStore = value;
        }
    }

    constructor(
        private customizationFrameworkService: CustomizationFrameworkService,
        private eFormService: EFormService,
        private yearService: YearService
    ) {
        this.customPageIndex = this.customizationFrameworkService.designPageIndexIncrementor;
        this.customizationFrameworkService.designPageIndexIncrementor++;
        const x = this.masterId;
    }

    ngOnInit() {
        if (this.canShowForm) {
            this.loadViewer();
        }

        this.subscriptions.push(this.yearService.observableDefaultYearStream$.subscribe(o => {
            this.getControlValues();
        }));

        this.subscriptions.push(this.customizationFrameworkService.observableFieldValuesRetrievedStream$.subscribe(fields => {
            this.getControlValues(fields);
        }));

        this.eFormService.observableControlValueChangedStream$.subscribe(c => {
            if (c.DesignPageIndex == this.customPageIndex && this.initialized && this.dataInitialized) {
                this.isDirty = true;
                this.isDirtyChange.emit(true);
            }
        });
        this.initialized = true;
    }

    loadViewer() {
        if (this.customPage) {
            this.config.Title = this.customPage.Name;
            this.config.UseInstance = false;
            this.config.ShowSubmittedBy = false;
            this.config.ViewMode = enmEFormViewModes.CustomPage;
            this.config.HideTitle = this.hideTitle;
            this.config.AllowPages = false;
            this.config.AllowTabs = false;
            this.config.ReadOnly = this.readOnly;
            this.config.CFDataStore = this.dataStore;
            // switch (this.customPage.ObjectName.toLowerCase()) {
            //   case 'student':
            //     this.config.PageObjectType = PageObjectTypes.Student;
            //     break;
            //   case 'staff':
            //     this.config.PageObjectType = PageObjectTypes.Staff;
            //     break;
            //   case 'other':
            //     this.config.PageObjectType = PageObjectTypes.Other;
            //     break;
            // }
            this.setFormData();
        }
    }

    setFormData() {
        this.customizationFrameworkService.reconcileChangesToCustomPage(this.customPage).pipe(take(1)).subscribe(formData => {
            this.customPage.FormData = JSON.stringify(formData);
            this.customPage.LocalVersion = this.customPage.SystemVersion;
            this.eFormService.selectedFormData = formData;
        });
    }

    getAllCustomFieldControls(): EFormControl[] {
        let formData = new EFormDataObject();
        try {
            formData = JSON.parse(this.customPage.FormData);
        } catch (err) {
            formData = new EFormDataObject();
        }

        const controls = this.eFormService.getAllFormControls(formData).filter(c => c.CustomField && c.CustomFieldName);
        return controls;
    }

    getControlValues(fields: CSTField[] = null): EFormControl[] {
        this.isDirty = false;
        this.dataInitialized = false; // prevents isDirty flag when initializing new data.
        let controls: EFormControl[] = [];
        if (this.eFormViewer) {
            controls = this.eFormViewer.getAllCustomFields(this.customPageIndex);
        }
        else {
            controls = this.getAllCustomFieldControls();
        }
        const fieldsToGet: CSTField[] = [];
        controls.forEach(controlToUpdate => {
            controlToUpdate.CustomFieldMasterId = this.masterId;
            const field = _.cloneDeep(this.customizationFrameworkService.customDataFields.find(f => f.FieldName == controlToUpdate.CustomFieldName));
            if (controlToUpdate.CustomFieldAllowControlToHandleOwnData && field && this.customizationFrameworkService.userCanViewField(field, controlToUpdate.Editable, controlToUpdate.CustomFieldAllowControlToHandleOwnData)) {
                const dataObj = this.getCustomFieldDataObject(field);
                if (dataObj && dataObj.Value != null) {
                    field.Value = dataObj.Value;
                    if (controlToUpdate) {
                        controlToUpdate.Value = dataObj.Value;
                    }
                }
                else if (this.masterId > 0 && fields) {
                    const fieldWithValue = fields.find(f => controlToUpdate.CustomFieldName == f.FieldName && f.MasterId == this.masterId);
                    if (fieldWithValue) {
                        controlToUpdate.Value = fieldWithValue.Value;
                        this.persistValue(field, controlToUpdate);
                    }
                }
                else {
                    controlToUpdate.Value = null;
                }
            }
        });
        setTimeout(() => {
            this.dataInitialized = true;
        }, 0);
        return controls;
    }

    getCustomFieldDataObject(field: CSTField): CustomFieldDataObject {
        return this.customizationFrameworkService.getCustomFieldDataObject(field, this.masterId, this.dataStore);
    }

    persistValue(field: CSTField, control: EFormControl) {
        this.customizationFrameworkService.persistValue(field, control.Editable, control.CustomFieldAllowControlToHandleOwnData, this.masterId, this.dataStore, false);
    }

    validateControls(): ErrorList[] {
        let errors: ErrorList[] = [];
        let controls: EFormControl[] = [];

        if (this.eFormViewer) {
            this.eFormViewer.validateButton.nativeElement.click();
            controls = this.eFormViewer.getAllCustomFields(this.customPageIndex);

            controls.forEach(c => {
                const field = this.customizationFrameworkService.customDataFields.find(o => o.FieldName == c.CustomFieldName);
                if (this.customizationFrameworkService.userCanEditField(field, c.Editable && !this.readOnly, c.CustomFieldAllowControlToHandleOwnData)) {
                    errors = errors.concat(c.validateFunction());
                }
            });
        }
        else {
            controls = this.getControlValues();
            controls.forEach(c => {
                const field = this.customizationFrameworkService.customDataFields.find(o => o.FieldName == c.CustomFieldName);
                if (this.customizationFrameworkService.userCanEditField(field, c.Editable && !this.readOnly, c.CustomFieldAllowControlToHandleOwnData)) {
                    errors = errors.concat(this.customizationFrameworkService.validateCustomField(field, c.Value, null, c.IsRequired));
                }
            });
        }

        return errors;
    }

    ngOnDestroy() {
        const controlsToRemove: EFormControl[] = this.eFormService.allCustomFieldControls.filter(control => control.DesignPageIndex == this.customPageIndex);

        controlsToRemove.forEach(controlToRemove => {
            let index: number = this.eFormService.allCustomFieldControls.findIndex(control => control.Id == controlToRemove.Id);
            if (index > -1) {
                this.eFormService.allCustomFieldControls.splice(index, 1);
            }

            index = this.eFormService.allFormControls.findIndex(control => control.Id == controlToRemove.Id);
            if (index > -1) {
                this.eFormService.allFormControls.splice(index, 1);
            }
        });
    }

    renderCompleted() {
        if (this.showErrorsOnInitialize) {
            this.showErrorsOnInitialize = false;
            this.eFormViewer.validateButton.nativeElement.click();
        }
    }

}
